import React from "react";
import { useTranslation } from "react-i18next";

import { Box, Divider, Grid2 as Grid, TextField, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { Select } from "components/forms";
import dayjs, { Dayjs } from "dayjs";
import { FormikProps } from "formik";
import { useDateFormatSelect } from "store";
import { CurrencyType } from "store/types";
import toIsoMidday from "utils/to-iso-midday";

import { LoanAmount } from "./form-fields/LoanAmount";
import { VAT } from "./form-fields/VAT";
import { FormValues } from "./schemas";

interface Props {
  formik: FormikProps<FormValues>;
  currency: CurrencyType;
  isFirstSegment?: boolean;
  disabledFields?: string[];
  hiddenFields?: string[];
  onStartDateChange?: (newStartDate: Dayjs) => void;
}

export const FacilityTermsDebtSection: React.FC<Props> = ({
  formik,
  currency,
  isFirstSegment = false,
  disabledFields = [],
  hiddenFields = [],
  onStartDateChange
}) => {
  const { t } = useTranslation("debt");
  const dateFormat = useDateFormatSelect();
  const isWithdrawal = formik.values.is_withdrawal && !formik.values.is_facility;

  return (
    <Box>
      <Box sx={{ mb: 5 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          {t("debtSection.title")}
        </Typography>
        <Divider />
      </Box>
      <Grid container spacing={5}>
        {isWithdrawal && (
          <Grid size={3}>
            <TextField
              fullWidth
              name="deal_title"
              label={t("debtSection.dealName")}
              placeholder={t("debtSection.dealNamePlaceholder")}
              variant="outlined"
              value={formik.values.deal_title || ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.deal_title && Boolean(formik.errors.deal_title)}
              helperText={formik.touched.deal_title && formik.errors.deal_title}
            />
          </Grid>
        )}
        <Grid size={3}>
          <LoanAmount formik={formik} currency={currency} label={isWithdrawal ? t("debtSection.loanAmount") : t("debtSection.commitment")} />
        </Grid>
        <Grid size={3}>
          <DesktopDatePicker
            format={dateFormat}
            views={["year", "month", "day"]}
            label={isFirstSegment ? t("debtSection.dateOfIssue") : t("debtSection.effectiveDate")}
            value={formik.values.start_date ? dayjs(formik.values.start_date) : null}
            onChange={(newValue) => {
              formik.setFieldValue("start_date", newValue ? toIsoMidday(newValue) : "");
              if (newValue && onStartDateChange) {
                onStartDateChange(newValue);
              }
            }}
            sx={{ width: "100%" }}
            slotProps={{
              textField: {
                error: formik.touched.start_date && Boolean(formik.errors.start_date),
                helperText: formik.touched.start_date && formik.errors.start_date
              }
            }}
          />
        </Grid>
        <Grid size={3}>
          <DesktopDatePicker
            format={dateFormat}
            views={["year", "month", "day"]}
            label={t("debtSection.maturityDate")}
            disabled={disabledFields.includes("maturity_date")}
            value={formik.values.maturity_date ? dayjs(formik.values.maturity_date) : null}
            onChange={(newValue) => formik.setFieldValue("maturity_date", newValue ? toIsoMidday(newValue) : "")}
            sx={{ width: "100%" }}
            slotProps={{
              textField: {
                error: formik.touched.maturity_date && Boolean(formik.errors.maturity_date),
                helperText: formik.touched.maturity_date && formik.errors.maturity_date
              }
            }}
          />
        </Grid>
        <Grid size={3}>
          <VAT formik={formik} disabled={disabledFields.includes("vat")} />
        </Grid>
        <Grid size={3} sx={{ display: hiddenFields.includes("loan_first_status_date") ? "none" : "block" }}>
          <DesktopDatePicker
            label={t("debtSection.statusDate")}
            format={dateFormat}
            views={["year", "month", "day"]}
            value={formik.values.loan_first_status_date ? dayjs(formik.values.loan_first_status_date) : null}
            onChange={(newValue) =>
              formik.setFieldValue("loan_first_status_date", newValue ? toIsoMidday(newValue) : "")
            }
            sx={{ width: "100%" }}
            disabled={disabledFields.includes("loan_first_status_date")}
            slotProps={{
              textField: {
                error: formik.touched.loan_first_status_date && Boolean(formik.errors.loan_first_status_date),
                helperText: formik.touched.loan_first_status_date && formik.errors.loan_first_status_date
              }
            }}
          />
        </Grid>
        <Grid size={3} sx={{ display: hiddenFields.includes("loan_status_frequency") ? "none" : "block" }}>
          <Select
            name="loan_status_frequency"
            label={t("debtSection.statusFrequency")}
            value={formik.values.loan_status_frequency}
            variant="outlined"
            fullWidth
            disabled={disabledFields.includes("loan_status_frequency")}
            items={[
              { key: "month", value: "month", text: t("common.monthly") },
              { key: "quarter", value: "quarter", text: t("common.quarterly") },
              { key: "semi-annual", value: "semi-annual", text: t("common.semiAnnual") },
              { key: "year", value: "year", text: t("common.annual") }
            ]}
            error={formik.touched.loan_status_frequency && Boolean(formik.errors.loan_status_frequency)}
            helperText={formik.touched.loan_status_frequency ? formik.errors.loan_status_frequency : undefined}
            onChange={formik.handleChange}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
